import { useFrame } from '@react-three/fiber';
import { memo, useEffect, useMemo } from 'react';
import { type Euler, Group, type Material, type Object3D } from 'three';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';

import { isMhrScene } from '@cartwhl/3d/gltf/utils';
import {
  usePlayerActorScene,
  usePlayerResolvedAnimation,
  usePlayerSettings,
  usePlayerTimeline,
} from '@cartwhl/3d/player';

import { ENABLE_MOTION_EDITOR_ADVANCED_GHOST_OPTIONS } from '../../feature-flags';
import {
  MotionEditorBoneMeshPicker,
  type MotionEditorBonePick,
  type MotionEditorBoneSelectHandler,
} from '../bonePicking';
import { removePoseMeshHighlightOverlays } from '../bonePicking/poseMeshHighlight';
import { getKeyPoseDragPreviewState } from '../keyPoseDragPreview';
import {
  normalizePlaybackFrame,
  sourceFrameToPlaybackFrame,
} from '../keyPoseFrameMapping';
import {
  DEFAULT_KEY_POSE_GHOST_COLOR,
  DEFAULT_KEY_POSE_GHOST_OPACITY,
  resolveKeyPoseGhostTint,
} from '../keyPoseGhostColors';
import { DEFAULT_KEY_POSE_GHOST_MATERIAL_VARIANT } from '../keyPoseGhostMaterialVariants';
import {
  type KeyPoseOnionSkinDescriptor,
  getKeyPoseOnionSkinColor,
  selectKeyPoseOnionSkinDescriptors,
} from '../keyPoseGhostOnionSkin';
import {
  hasVisibleUserKeyPoseGhostsForReference,
  isKeyPoseRenderableInScene,
  selectKeyPoseRecordsForReference,
  shouldRenderKeyPoseGhostAtPlaybackFrame,
} from '../keyPoseSelectors';
import {
  applyKeyPoseSnapshotToScene,
  captureKeyPoseSnapshotFromSourceScene,
  createKeyPoseSnapshotFromSourcePose,
  hasKeyPoseSkeletonSnapshotTransforms,
} from '../keyPoseSnapshots';
import { useStore } from '../keyPoseStore';
import type {
  KeyPoseFkJointId,
  KeyPoseGhostMaterialVariant,
  KeyPoseGhostOptionsState,
  KeyPoseId,
  KeyPoseRecord,
} from '../types';
import {
  applyEditableGhostSceneMaterials,
  applyGhostDepthPrepassMaterials,
  applyGhostSceneMaterials,
  applyKeyPoseSnapshotToFrozenGhostScene,
  applyTexturedGhostSceneMaterials,
  freezeGhostScene,
} from './ghostSceneRenderUtils';

interface KeyPoseGhostsProps {
  actorIds: readonly string[];
  actorGhostColors?: Readonly<Record<string, string | undefined>>;
  editableKeyPoseId: KeyPoseId | null;
  editableReferenceName: string | null;
  hoveredJointIds?: readonly KeyPoseFkJointId[];
  liveActorReplacementKeyPoseId: KeyPoseId | null;
  onBoneHover?: (pick: MotionEditorBonePick | null) => void;
  onBoneSelect?: MotionEditorBoneSelectHandler;
  raycastEnabled?: boolean;
  sceneRotation?: Euler;
  selectedJointIds?: readonly KeyPoseFkJointId[];
}

interface KeyPoseActorGhostsProps {
  actorId: string;
  ghostColor: string;
  ghostOptions: KeyPoseGhostOptionsState;
  draggingKeyPoseId: KeyPoseId | null;
  editableKeyPoseId: KeyPoseId | null;
  editableReferenceName: string | null;
  hasVisibleUserKeyPoseGhosts: boolean;
  hoveredJointIds?: readonly KeyPoseFkJointId[];
  keyPoses: readonly KeyPoseRecord[];
  liveActorReplacementKeyPoseId: KeyPoseId | null;
  onBoneHover?: (pick: MotionEditorBonePick | null) => void;
  onBoneSelect?: MotionEditorBoneSelectHandler;
  previewFrame: number | null;
  previewByKeyPoseId: KeyPoseGhostPreviewById;
  raycastEnabled?: boolean;
  sceneRotation?: Euler;
  selectedJointIds?: readonly KeyPoseFkJointId[];
  useActorGhostColor: boolean;
}

interface KeyPoseSavedGhostProps {
  actorId: string;
  actorScene: Object3D | null;
  bvh: ReturnType<typeof usePlayerResolvedAnimation>['bvh'];
  draggingKeyPoseId: KeyPoseId | null;
  ghostColor: string;
  ghostMaterialVariant: KeyPoseGhostMaterialVariant;
  ghostOpacity: number;
  editableKeyPoseId: KeyPoseId | null;
  editableReferenceName: string | null;
  hoveredJointIds?: readonly KeyPoseFkJointId[];
  keyPose: KeyPoseRecord;
  liveActorReplacementKeyPoseId: KeyPoseId | null;
  onBoneHover?: (pick: MotionEditorBonePick | null) => void;
  onBoneSelect?: MotionEditorBoneSelectHandler;
  previewFrame: number | null;
  previewSkeletonSnapshot?: KeyPoseRecord['skeletonSnapshot'];
  raycastEnabled?: boolean;
  sceneRotation?: Euler;
  selectedJointIds?: readonly KeyPoseFkJointId[];
}

interface KeyPoseGhostPrimitiveProps {
  ghost: KeyPoseGhostScene;
  enableEditingSurfaces?: boolean;
  hoveredJointIds?: readonly KeyPoseFkJointId[];
  onBoneHover?: (pick: MotionEditorBonePick | null) => void;
  onBoneSelect?: MotionEditorBoneSelectHandler;
  raycastEnabled?: boolean;
  selectedJointIds?: readonly KeyPoseFkJointId[];
  visible?: boolean;
}

interface KeyPoseGhostScene {
  id: string;
  materials: Material[];
  object: Object3D;
  visualMode: KeyPoseGhostVisualMode;
}

type KeyPoseGhostPreviewById = Record<
  string,
  Pick<KeyPoseRecord, 'skeletonSnapshot'> | undefined
>;

type KeyPoseGhostVisualMode = 'ghost' | 'editable';

const GHOST_PREVIEW_OPACITY = 0.16;
const GHOST_RENDER_ORDER = 10;
const GHOST_DEPTH_PREPASS_RENDER_ORDER = GHOST_RENDER_ORDER - 1;
const PREVIEW_KEY_POSE_GHOST_MATERIAL_VARIANT: KeyPoseGhostMaterialVariant =
  'flatTint';
const TEXTURED_TRANSPARENT_GHOST_TINT_AMOUNT = 0.12;
const TEXTURED_SHELL_GHOST_TINT_AMOUNT = 0.04;
const TEXTURED_GHOST_OPACITY_MULTIPLIER = 2.25;
const MAX_TEXTURED_GHOST_OPACITY = 0.52;

function KeyPoseGhosts({
  actorIds,
  actorGhostColors,
  editableKeyPoseId,
  editableReferenceName,
  hoveredJointIds,
  liveActorReplacementKeyPoseId,
  onBoneHover,
  onBoneSelect,
  raycastEnabled,
  sceneRotation,
  selectedJointIds,
}: KeyPoseGhostsProps) {
  const keyPoseStore = useStore();
  const keyPoseSnapshot = keyPoseStore.state;
  const draggingKeyPoseId = keyPoseSnapshot.editing.dragging
    ? keyPoseSnapshot.editing.draggingKeyPoseId
    : null;
  const keyPosesByActorId = useMemo(() => {
    const nextKeyPosesByActorId: Record<
      string,
      {
        hasVisibleUserKeyPoseGhosts: boolean;
        keyPoses: KeyPoseRecord[];
      }
    > = {};

    for (const actorId of actorIds) {
      const hasVisibleUserKeyPoseGhosts =
        hasVisibleUserKeyPoseGhostsForReference(keyPoseSnapshot, actorId);
      nextKeyPosesByActorId[actorId] = {
        hasVisibleUserKeyPoseGhosts,
        keyPoses: selectKeyPoseRecordsForReference(
          keyPoseSnapshot,
          actorId,
        ).filter((keyPose) =>
          isKeyPoseRenderableInScene(keyPose, hasVisibleUserKeyPoseGhosts),
        ) as KeyPoseRecord[],
      };
    }

    return nextKeyPosesByActorId;
  }, [
    actorIds,
    keyPoseSnapshot.referencesByName,
    keyPoseSnapshot.keyPosesById,
  ]);

  return (
    <>
      {actorIds.map((actorId) => {
        const actorKeyPoseState = keyPosesByActorId[actorId];

        return (
        <KeyPoseActorGhosts
          key={actorId}
          actorId={actorId}
          ghostColor={
            actorGhostColors?.[actorId] ?? DEFAULT_KEY_POSE_GHOST_COLOR
          }
          ghostOptions={keyPoseSnapshot.ghostOptions}
          draggingKeyPoseId={draggingKeyPoseId}
          editableKeyPoseId={editableKeyPoseId}
          editableReferenceName={editableReferenceName}
          hasVisibleUserKeyPoseGhosts={
            actorKeyPoseState?.hasVisibleUserKeyPoseGhosts ?? false
          }
          hoveredJointIds={hoveredJointIds}
          keyPoses={actorKeyPoseState?.keyPoses ?? []}
          liveActorReplacementKeyPoseId={liveActorReplacementKeyPoseId}
          onBoneHover={onBoneHover}
          onBoneSelect={onBoneSelect}
          previewByKeyPoseId={keyPoseSnapshot.editing.previewByKeyPoseId}
          raycastEnabled={raycastEnabled}
          previewFrame={
            keyPoseSnapshot.hoverPreview?.referenceName === actorId
              ? keyPoseSnapshot.hoverPreview.frame
              : null
          }
          sceneRotation={sceneRotation}
          selectedJointIds={selectedJointIds}
          useActorGhostColor={Boolean(actorGhostColors?.[actorId])}
        />
        );
      })}
    </>
  );
}

export default memo(KeyPoseGhosts);

const KeyPoseActorGhosts = memo(function KeyPoseActorGhosts({
  actorId,
  ghostColor,
  ghostOptions,
  draggingKeyPoseId,
  editableKeyPoseId,
  editableReferenceName,
  hasVisibleUserKeyPoseGhosts,
  hoveredJointIds,
  keyPoses,
  liveActorReplacementKeyPoseId,
  onBoneHover,
  onBoneSelect,
  previewByKeyPoseId,
  previewFrame,
  raycastEnabled,
  sceneRotation,
  selectedJointIds,
  useActorGhostColor,
}: KeyPoseActorGhostsProps) {
  const actorScene = usePlayerActorScene(actorId);
  const { bvh } = usePlayerResolvedAnimation(actorId);
  const timeline = usePlayerTimeline(actorId);
  const {
    settings: {
      playback: { frameRate: playbackFrameRate },
    },
  } = usePlayerSettings();
  const maxPlaybackFrame = Math.max(timeline.duration.frames - 1, 0);
  const currentPlaybackFrame = normalizePlaybackFrame(
    timeline.currentFrame,
    maxPlaybackFrame,
  );
  const onionSkinOptions = {
    ...ghostOptions.onionSkin,
    enabled:
      ENABLE_MOTION_EDITOR_ADVANCED_GHOST_OPTIONS &&
      ghostOptions.onionSkin.enabled,
  };
  const keyPosesForRendering = useMemo(() => {
    return keyPoses.filter((keyPose) =>
      shouldRenderKeyPoseGhostAtPlaybackFrame({
        keyPose,
        keyPosePlaybackFrame: sourceFrameToPlaybackFrame({
          bvh,
          frame: keyPose.frame,
          maxPlaybackFrame,
          playbackFrameRate,
        }),
        currentPlaybackFrame,
        liveActorReplacementKeyPoseId,
        draggingKeyPoseId,
        hasVisibleUserKeyPoseGhosts,
      }),
    );
  }, [
    bvh,
    currentPlaybackFrame,
    draggingKeyPoseId,
    hasVisibleUserKeyPoseGhosts,
    keyPoses,
    liveActorReplacementKeyPoseId,
    maxPlaybackFrame,
    playbackFrameRate,
  ]);
  const onionSkinDescriptorsByKeyPoseId = useMemo(() => {
    if (!onionSkinOptions.enabled) {
      return new Map<KeyPoseId, KeyPoseOnionSkinDescriptor>();
    }

    return new Map(
      selectKeyPoseOnionSkinDescriptors({
        currentPlaybackFrame,
        entries: keyPosesForRendering
          .filter((keyPose) => !keyPose.hidden)
          .map((keyPose) => ({
            keyPoseId: keyPose.id,
            playbackFrame: sourceFrameToPlaybackFrame({
              bvh,
              frame: keyPose.frame,
              maxPlaybackFrame,
              playbackFrameRate,
            }),
          })),
        options: onionSkinOptions,
      }).map((descriptor) => [descriptor.keyPoseId, descriptor]),
    );
  }, [
    bvh,
    currentPlaybackFrame,
    keyPosesForRendering,
    maxPlaybackFrame,
    onionSkinOptions.enabled,
    onionSkinOptions.ghostsPerDirection,
    onionSkinOptions.maxOpacity,
    onionSkinOptions.minOpacity,
    playbackFrameRate,
  ]);
  const renderedKeyPoses = useMemo(() => {
    if (!onionSkinOptions.enabled) {
      return keyPosesForRendering;
    }

    return keyPosesForRendering.filter(
      (keyPose) =>
        onionSkinDescriptorsByKeyPoseId.has(keyPose.id) ||
        (actorId === editableReferenceName &&
          keyPose.id === editableKeyPoseId) ||
        (Boolean(keyPose.hidden) && keyPose.frame === previewFrame),
    );
  }, [
    actorId,
    editableKeyPoseId,
    editableReferenceName,
    keyPosesForRendering,
    onionSkinDescriptorsByKeyPoseId,
    onionSkinOptions.enabled,
    previewFrame,
  ]);

  const previewGhost = useMemo(() => {
    if (!actorScene || previewFrame === null || !bvh) {
      return null;
    }

    const previewMatchesSavedKeyPose = keyPoses.some(
      (keyPose) => keyPose.frame === previewFrame,
    );
    if (previewMatchesSavedKeyPose) {
      return null;
    }

    const previewSnapshot = captureKeyPoseSnapshotFromSourceScene({
      bvh,
      frame: previewFrame,
      scene: actorScene,
    });
    if (!previewSnapshot) {
      return null;
    }

    return createKeyPoseGhostScene(
      `hover-preview-${actorId}-${previewSnapshot.frame}`,
      actorScene,
      previewSnapshot.skeletonSnapshot,
      sceneRotation,
      'ghost',
      DEFAULT_KEY_POSE_GHOST_COLOR,
      GHOST_PREVIEW_OPACITY,
      PREVIEW_KEY_POSE_GHOST_MATERIAL_VARIANT,
    );
  }, [
    actorId,
    actorScene,
    bvh,
    ghostColor,
    keyPoses,
    previewFrame,
    sceneRotation,
  ]);

  useEffect(() => {
    if (!previewGhost) {
      return;
    }

    return () => disposeKeyPoseGhostScene(previewGhost);
  }, [previewGhost]);

  if (renderedKeyPoses.length === 0 && !previewGhost) {
    return null;
  }

  return (
    <group>
      {renderedKeyPoses.map((keyPose) => {
        const onionSkinDescriptor = onionSkinDescriptorsByKeyPoseId.get(
          keyPose.id,
        );
        const { color: stampGhostColor, opacity: stampGhostOpacity } =
          resolveKeyPoseGhostAppearance({
            keyPose,
            actorGhostColor: ghostColor,
            onionSkinDescriptor,
            useActorGhostColor,
          });

        return (
          <KeyPoseSavedGhost
            key={keyPose.id}
            actorId={actorId}
            actorScene={actorScene}
            bvh={bvh}
            draggingKeyPoseId={draggingKeyPoseId}
            ghostColor={stampGhostColor}
            ghostMaterialVariant={
              ghostOptions.materialVariant ??
              DEFAULT_KEY_POSE_GHOST_MATERIAL_VARIANT
            }
            ghostOpacity={stampGhostOpacity}
            editableKeyPoseId={editableKeyPoseId}
            editableReferenceName={editableReferenceName}
            hoveredJointIds={hoveredJointIds}
            keyPose={keyPose}
            liveActorReplacementKeyPoseId={liveActorReplacementKeyPoseId}
            onBoneHover={onBoneHover}
            onBoneSelect={onBoneSelect}
            previewFrame={previewFrame}
            previewSkeletonSnapshot={
              previewByKeyPoseId[keyPose.id]?.skeletonSnapshot
            }
            raycastEnabled={raycastEnabled}
            sceneRotation={sceneRotation}
            selectedJointIds={selectedJointIds}
          />
        );
      })}
      {previewGhost ? (
        <KeyPoseGhostPrimitive key={previewGhost.id} ghost={previewGhost} />
      ) : null}
    </group>
  );
});

function KeyPoseSavedGhost({
  actorId,
  actorScene,
  bvh,
  draggingKeyPoseId,
  ghostColor,
  ghostMaterialVariant: stageGhostMaterialVariant,
  ghostOpacity,
  editableKeyPoseId,
  editableReferenceName,
  hoveredJointIds,
  keyPose,
  liveActorReplacementKeyPoseId,
  onBoneHover,
  onBoneSelect,
  previewFrame,
  previewSkeletonSnapshot,
  raycastEnabled,
  sceneRotation,
  selectedJointIds,
}: KeyPoseSavedGhostProps) {
  const isEditableKeyPose =
    actorId === editableReferenceName && keyPose.id === editableKeyPoseId;
  const isLiveActorReplacementKeyPose =
    keyPose.id === liveActorReplacementKeyPoseId;
  const isDragPreviewKeyPose =
    draggingKeyPoseId !== null && keyPose.id === draggingKeyPoseId;
  const isHiddenPreviewKeyPose =
    Boolean(keyPose.hidden) && keyPose.frame === previewFrame;
  const enableEditingSurfaces = isEditableKeyPose || isDragPreviewKeyPose;
  const ghostMaterialVariant: KeyPoseGhostMaterialVariant =
    isHiddenPreviewKeyPose
      ? PREVIEW_KEY_POSE_GHOST_MATERIAL_VARIANT
      : stageGhostMaterialVariant;
  const resolvedGhostColor = isHiddenPreviewKeyPose
    ? DEFAULT_KEY_POSE_GHOST_COLOR
    : ghostColor;
  const resolvedGhostOpacity = isHiddenPreviewKeyPose
    ? GHOST_PREVIEW_OPACITY
    : ghostOpacity;
  const visualMode: KeyPoseGhostVisualMode =
    enableEditingSurfaces || isLiveActorReplacementKeyPose
      ? 'editable'
      : 'ghost';
  const skeletonSnapshot = previewSkeletonSnapshot ?? keyPose.skeletonSnapshot;
  const resolvedSkeletonSnapshot = useMemo(() => {
    if (hasKeyPoseSkeletonSnapshotTransforms(skeletonSnapshot)) {
      return skeletonSnapshot;
    }

    return (
      createKeyPoseSnapshotFromSourcePose({
        bvh,
        scene: actorScene,
        sourcePose: keyPose.sourcePose,
      })?.skeletonSnapshot ?? skeletonSnapshot
    );
  }, [actorScene, bvh, keyPose.sourcePose, skeletonSnapshot]);

  const ghost = useMemo(() => {
    if (
      !actorScene ||
      !hasKeyPoseSkeletonSnapshotTransforms(resolvedSkeletonSnapshot)
    ) {
      return null;
    }

    return createKeyPoseGhostScene(
      keyPose.id,
      actorScene,
      resolvedSkeletonSnapshot,
      sceneRotation,
      visualMode,
      resolvedGhostColor,
      resolvedGhostOpacity,
      ghostMaterialVariant,
    );
  }, [
    actorScene,
    ghostMaterialVariant,
    keyPose.id,
    resolvedSkeletonSnapshot,
    resolvedGhostColor,
    resolvedGhostOpacity,
    sceneRotation,
    visualMode,
  ]);

  useEffect(() => {
    if (!ghost) {
      return;
    }

    return () => disposeKeyPoseGhostScene(ghost);
  }, [ghost]);

  if (!ghost) {
    return null;
  }

  return (
    <KeyPoseGhostPrimitive
      ghost={ghost}
      enableEditingSurfaces={enableEditingSurfaces}
      hoveredJointIds={hoveredJointIds}
      onBoneHover={onBoneHover}
      onBoneSelect={onBoneSelect}
      raycastEnabled={raycastEnabled}
      selectedJointIds={selectedJointIds}
      visible={
        !keyPose.hidden ||
        isEditableKeyPose ||
        isLiveActorReplacementKeyPose ||
        isHiddenPreviewKeyPose
      }
    />
  );
}

function KeyPoseGhostPrimitive({
  ghost,
  enableEditingSurfaces = false,
  hoveredJointIds,
  onBoneHover,
  onBoneSelect,
  raycastEnabled,
  selectedJointIds,
  visible = true,
}: KeyPoseGhostPrimitiveProps) {
  const isEditable = ghost.visualMode === 'editable';

  return (
    <group visible={visible}>
      <primitive object={ghost.object} />
      {isEditable && visible ? <KeyPoseGhostDragSync ghost={ghost} /> : null}
      {enableEditingSurfaces && visible ? (
        <MotionEditorBoneMeshPicker
          scene={ghost.object}
          hoveredJointIds={hoveredJointIds}
          onBoneHover={onBoneHover}
          onBoneSelect={onBoneSelect}
          raycastEnabled={raycastEnabled}
          selectedJointIds={selectedJointIds}
        />
      ) : null}
    </group>
  );
}

function KeyPoseGhostDragSync({ ghost }: { ghost: KeyPoseGhostScene }) {
  useFrame(() => {
    const dragPreview = getKeyPoseDragPreviewState();
    if (dragPreview.keyPoseId !== ghost.id || !dragPreview.preview) {
      return;
    }

    applyKeyPoseSnapshotToFrozenGhostScene(
      ghost.object,
      dragPreview.preview.skeletonSnapshot,
    );
  });

  return null;
}

function disposeKeyPoseGhostScene(ghost: KeyPoseGhostScene) {
  for (const material of ghost.materials) {
    material.dispose();
  }
}

function createKeyPoseGhostScene(
  id: string,
  actorScene: Object3D,
  skeletonSnapshot: KeyPoseRecord['skeletonSnapshot'],
  sceneRotation: Euler | undefined,
  visualMode: KeyPoseGhostVisualMode,
  color: string,
  opacity: number,
  materialVariant: KeyPoseGhostMaterialVariant = DEFAULT_KEY_POSE_GHOST_MATERIAL_VARIANT,
) {
  const ghostScene = SkeletonUtils.clone(actorScene) as Object3D;
  removePoseMeshHighlightOverlays(ghostScene);
  if (sceneRotation && !isMhrScene(actorScene)) {
    ghostScene.rotation.copy(sceneRotation);
  }

  applyKeyPoseSnapshotToScene(ghostScene, skeletonSnapshot);
  let materials: Material[];
  let object: Object3D = ghostScene;
  if (visualMode === 'editable') {
    materials = applyEditableGhostSceneMaterials(ghostScene);
  } else if (materialVariant === 'flatTint') {
    materials = applyGhostSceneMaterials(ghostScene, {
      color,
      opacity,
      renderOrder: GHOST_RENDER_ORDER,
    });
  } else if (materialVariant === 'texturedTransparent') {
    materials = applyTexturedGhostSceneMaterials(ghostScene, {
      color,
      opacity,
      renderOrder: GHOST_RENDER_ORDER,
      tintAmount: TEXTURED_TRANSPARENT_GHOST_TINT_AMOUNT,
    });
  } else {
    const depthScene = SkeletonUtils.clone(ghostScene) as Object3D;
    const depthMaterials = applyGhostDepthPrepassMaterials(depthScene, {
      renderOrder: GHOST_DEPTH_PREPASS_RENDER_ORDER,
    });
    const visibleMaterials = applyTexturedGhostSceneMaterials(ghostScene, {
      color,
      opacity: getTexturedGhostOpacity(opacity),
      renderOrder: GHOST_RENDER_ORDER,
      tintAmount: TEXTURED_SHELL_GHOST_TINT_AMOUNT,
    });
    const group = new Group();

    group.add(depthScene, ghostScene);
    freezeGhostScene(depthScene);
    object = group;
    materials = [...depthMaterials, ...visibleMaterials];
  }
  freezeGhostScene(ghostScene);

  return {
    id,
    materials,
    object,
    visualMode,
  } satisfies KeyPoseGhostScene;
}

function getTexturedGhostOpacity(opacity: number) {
  return Math.min(
    MAX_TEXTURED_GHOST_OPACITY,
    Math.max(0, opacity * TEXTURED_GHOST_OPACITY_MULTIPLIER),
  );
}

function resolveKeyPoseGhostAppearance({
  keyPose,
  actorGhostColor,
  onionSkinDescriptor,
  useActorGhostColor,
}: {
  keyPose: KeyPoseRecord;
  actorGhostColor: string;
  onionSkinDescriptor: KeyPoseOnionSkinDescriptor | undefined;
  useActorGhostColor: boolean;
}) {
  if (onionSkinDescriptor && !useActorGhostColor) {
    return {
      color: getKeyPoseOnionSkinColor(onionSkinDescriptor.direction),
      opacity: onionSkinDescriptor.opacity,
    };
  }

  if (useActorGhostColor) {
    return {
      color: actorGhostColor,
      opacity: DEFAULT_KEY_POSE_GHOST_OPACITY,
    };
  }

  return resolveKeyPoseGhostTint(keyPose);
}
